import { motion } from "framer-motion";
import {
  Repeat, Users, WifiOff, Download, Star, Trophy, ListOrdered, Mail, GitBranch, Swords, CalendarClock,
  Crown, MonitorPlay, ShieldCheck, Gauge, Link2, Layers, ArrowRight,
} from "lucide-react";

const PRODUCTS = [
  {
    id: "openplay", tag: "Open Play", title: "Open-Play Queue OS", icon: Repeat, span: "lg:col-span-7",
    img: "https://images.unsplash.com/photo-1692288720754-743fbd1f2155?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    desc: "Continuous court rotations that keep every player moving. Local-first and outdoor-ready.",
    features: [
      [Repeat, "FIFO rotations with sit-to-play fairness"],
      [Download, "Import confirmed players straight from Reclub"],
      [WifiOff, "Works offline — sync when you're back online"],
      [Star, "Publish a session and let players give Casual Play ratings"],
    ],
  },
  {
    id: "tournament", tag: "Tournaments", title: "Pro Tournament Suite", icon: Trophy, span: "lg:col-span-5",
    desc: "Registration to podium — brackets, payments and players in one place.",
    features: [
      [ListOrdered, "Category limits with automatic waitlists"],
      [GitBranch, "Round robin pools, playoffs & elimination brackets"],
      [Mail, "Branded emails for confirmations, waitlists & rejections"],
      [Users, "Singles & doubles with partner invites"],
    ],
  },
  {
    id: "clubwars", tag: "Club Wars", title: "Club vs Club Team Events", icon: Swords, span: "lg:col-span-5",
    desc: "Dual-meet team battles with lineups, schedules and live standings.",
    features: [
      [CalendarClock, "Smart court schedules with player rest built in"],
      [Crown, "Captain portal for lineups"],
      [MonitorPlay, "Live TV standings & scoreboards"],
    ],
  },
  {
    id: "ratings", tag: "Ratings", title: "Verified Player Ratings", icon: Gauge, span: "lg:col-span-7",
    img: "https://images.unsplash.com/photo-1768330187404-59e46cf222c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    desc: "One trusted 0.0–6.0+ scale, from Newbie to Professional.",
    features: [
      [ShieldCheck, "Official Performance rating from verified match results"],
      [Layers, "Assessment: 50% organizers, 30% event players, 20% acquaintances"],
      [Gauge, "Optional Composite rating that blends both"],
      [Link2, "Secure rating-request links you can share"],
    ],
  },
];

function ProductBlock({ p, i, nav }) {
  const Icon = p.icon;
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
      className={`${p.span} relative overflow-hidden rounded-3xl glow-border grain p-7 sm:p-10 bg-surface`} data-testid={`service-${p.id}`}>
      {p.img && <img src={p.img} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.13 }} />}
      <div className="relative z-10">
        <div className="w-12 h-12 rounded-xl glass flex items-center justify-center mb-6"><Icon className="w-6 h-6 text-primary-c" /></div>
        <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">{p.tag}</span>
        <h3 className="font-display font-bold text-2xl sm:text-3xl mt-2">{p.title}</h3>
        <p className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>{p.desc}</p>
        <ul className="mt-6 space-y-3">
          {p.features.map(([FIcon, text]) => (
            <li key={text} className="flex items-start gap-3 text-sm" style={{ color: "var(--text-2)" }}>
              <FIcon className="w-4 h-4 text-primary-c mt-0.5 shrink-0" /> {text}
            </li>
          ))}
        </ul>
        <button data-testid={`service-${p.id}-cta`} onClick={() => nav("/login")}
          className="mt-8 inline-flex items-center gap-2 font-bold text-primary-c hover:gap-3 transition-all">
          Get started <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}

export const Products = ({ nav }) => (
  <section id="services" className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
    <div className="mb-14">
      <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Everything is live</span>
      <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3">One platform, every court.</h2>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {PRODUCTS.map((p, i) => <ProductBlock key={p.id} p={p} i={i} nav={nav} />)}
    </div>
  </section>
);
